import OnboardingService from "@/services/onboarding/onboarding.service";

/**
 * Cache do status de onboarding, chaveado por PDV.
 *
 * Existe porque o `router.beforeEach` roda a cada navegação e não pode fazer
 * uma request por transição. Trocar de PDV faz `window.location.reload()`
 * (ver header.vue), então o cache morre naturalmente na troca de loja — não
 * há risco de mostrar o status da loja errada.
 */
const TTL_MS = 60_000;

export default {
  namespaced: true,

  state: () => ({
    status: null, // resposta do backend
    pdvId: null, // PDV a que este cache pertence
    fetchedAt: 0,
    loading: false,
  }),

  getters: {
    /** ÚNICA fonte do redirect. Sem status carregado ⇒ não bloqueia (fail-open). */
    requiresAction: (state) => state.status?.requiresAction === true,
    onboardingState: (state) => state.status?.state ?? null,
    onboardingUrl: (state) => state.status?.onboardingUrl ?? null,
    rejectReasons: (state) => state.status?.rejectReasons ?? [],
    pendingDocuments: (state) => state.status?.pendingDocuments ?? [],
    status: (state) => state.status,
    isFresh: (state) => (pdvId) =>
      state.pdvId === pdvId &&
      state.status !== null &&
      Date.now() - state.fetchedAt < TTL_MS,
  },

  mutations: {
    SET_STATUS(state, { status, pdvId }) {
      state.status = status;
      state.pdvId = pdvId;
      state.fetchedAt = Date.now();
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    CLEAR(state) {
      state.status = null;
      state.pdvId = null;
      state.fetchedAt = 0;
    },
  },

  actions: {
    /**
     * Busca o status respeitando o cache. `force: true` ignora o TTL — usado
     * no login (para o guard já decidir com cache quente, sem flash de tela) e
     * no botão "Verificar agora".
     */
    async ensureStatus({ state, getters, commit }, { pdvId, force = false } = {}) {
      if (!pdvId) return null;
      if (!force && getters.isFresh(pdvId)) return state.status;

      // Evita rajada de requests concorrentes numa navegação rápida.
      if (state.loading) return state.status;

      commit("SET_LOADING", true);
      try {
        const status = force
          ? await OnboardingService.refresh(pdvId)
          : await OnboardingService.getStatus(pdvId);
        commit("SET_STATUS", { status, pdvId });
        return status;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    clear({ commit }) {
      commit("CLEAR");
    },
  },
};
